import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Paper,
  Stack,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { formatDateField } from "../../../../../../base/FormatTime";

const Section = ({ title, children }) => (
  <Paper variant="outlined" sx={{ p: 2.5, mb: 2.5 }}>
    <Typography
      variant="subtitle1"
      component="h3"
      sx={{ fontWeight: "bold", mb: 2, color: "text.primary" }}
    >
      {title}
    </Typography>
    {children}
  </Paper>
);

const DetailItem = ({ label, value, isLast = false }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      py: 1.5,
      borderBottom: !isLast ? 1 : 0,
      borderColor: "divider",
    }}
  >
    <Typography
      variant="body2"
      sx={{ color: "text.secondary", flex: 1, pr: 2 }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{ fontWeight: "medium", color: "text.primary", textAlign: "right" }}
    >
      {value || "N/A"}
    </Typography>
  </Box>
);

const getChoiceLabel = (value) => {
  if (value === true) return "Yes";
  if (value === false) return "No";
  return "Not answered";
};

const InitialOralHealthAssessmentDetailsModal = ({
  open,
  handleClose,
  assessment,
}) => {
  if (!assessment) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" component="div">
            Initial Oral Health Assessment Details
          </Typography>
          <IconButton onClick={handleClose} edge="end">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ bgcolor: "grey.50", p: 3 }}>
        <Stack spacing={2}>
          <Section title="General Information">
            <DetailItem label="Service User" value={assessment.client_name} />
            <DetailItem
              label="Date of Assessment"
              value={formatDateField(assessment.created_at)}
            />
            <DetailItem label="Created By" value={assessment.created_by_name} />
            <DetailItem
              label="Care Plan"
              isLast
              value={assessment.care_plan_name}
            />
          </Section>

          <Section title="Dentures">
            <DetailItem
              label="Has Dentures"
              value={getChoiceLabel(assessment.has_dentures)}
              isLast={!assessment.has_dentures}
            />
            {assessment.has_dentures && (
              <>
                <DetailItem
                  label="Denture Types"
                  value={assessment.denture_types?.join(", ")}
                />
                <DetailItem
                  label="Dentures Labelled"
                  value={getChoiceLabel(assessment.are_dentures_labelled)}
                />
                <DetailItem
                  label="Denture Age"
                  value={assessment.denture_age}
                  isLast
                />
              </>
            )}
          </Section>

          <Section title="Oral Health Problems">
            <DetailItem
              label="Experiencing Problems"
              value={getChoiceLabel(assessment.has_oral_problems)}
              isLast={!assessment.has_oral_problems}
            />
            {assessment.has_oral_problems && (
              <>
                <DetailItem
                  label="Problem Types"
                  value={assessment.oral_problems_list?.join(", ")}
                />
                <DetailItem
                  label="Problem Description"
                  value={assessment.oral_problems_description}
                  isLast
                />
              </>
            )}
          </Section>

          <Section title="Dental Care & Action Plan">
            <DetailItem
              label="Requires Urgent Checkup"
              value={getChoiceLabel(assessment.requires_urgent_checkup)}
              isLast={!assessment.requires_urgent_checkup}
            />
            {assessment.requires_urgent_checkup && (
              <DetailItem
                label="Urgent Checkup Details"
                value={assessment.urgent_checkup_details}
                isLast
              />
            )}
            <DetailItem
              label="Last Dentist Visit"
              value={assessment.last_dentist_visit}
            />
            <DetailItem
              label="Registered with Dentist"
              value={getChoiceLabel(assessment.is_registered_with_dentist)}
            />
            <DetailItem
              label="Action to be Taken"
              value={assessment.action_to_be_taken}
              isLast
            />
          </Section>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
        <Button onClick={handleClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InitialOralHealthAssessmentDetailsModal;
